import {
    AlertTriangle,
    ChevronLeft,
    Info,
    LoaderCircle,
    Sparkles,
    Zap,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './CreateIncentiveTemplateScreen.module.css'

const parseActiveStore = (raw) => {
    try {
        return raw && raw.trim() !== '' ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

// ── Quick-start templates for first-time users ───────────────────────────────
const QUICK_START_PLANS = [
    {
        label: 'Service Commission',
        name: 'Senior Stylist Service Commission',
        description:
            'Flat percentage on all service revenue generated by the stylist. Commission computed monthly from session data. Ideal for experienced stylists with consistent clientele.',
    },
    {
        label: 'Category-based Commission',
        name: 'Category Commission Plan',
        description:
            'Different commission percentages for different service types — higher % for premium services like hair color and bridal. Encourages up-selling.',
    },
    {
        label: 'Slab Commission',
        name: 'Performance Slab Plan',
        description:
            'Tiered commission that increases as monthly revenue grows. Incentivises staff to push past targets. Modelled on income tax slab logic — earning more always benefits the stylist.',
    },
    {
        label: 'Target Bonus Plan',
        name: 'Monthly Target Bonus',
        description:
            'Fixed bonus unlocked when a monthly revenue target is hit. Additional overachievement commission on revenue above the target.',
    },
]

// ── Component ─────────────────────────────────────────────────────────────────
const CreateIncentiveTemplateScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    // ── Form state ───────────────────────────────────────────────
    const [formData, setFormData] = useState({
        templateName: '',
        templateDescription: '',
    })
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [showQuickStart, setShowQuickStart] = useState(true)

    // ── Derived ──────────────────────────────────────────────────
    const nameCharCount = formData.templateName.length
    const descCharCount = formData.templateDescription.length

    // ── Handlers ─────────────────────────────────────────────────
    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    const applyQuickStart = (plan) => {
        setFormData({
            templateName: plan.name,
            templateDescription: plan.description,
        })
        setErrors({})
        setShowQuickStart(false)
    }

    // ── Validation ───────────────────────────────────────────────
    const validate = () => {
        const newErrors = {}

        if (!formData.templateName.trim()) {
            newErrors.templateName = 'Plan name is required.'
        } else if (formData.templateName.trim().length > 200) {
            newErrors.templateName = 'Plan name must not exceed 200 characters.'
        }

        if (!formData.templateDescription.trim()) {
            newErrors.templateDescription = 'Description is required.'
        }

        return newErrors
    }

    // ── API call ─────────────────────────────────────────────────
    const createIncentiveTemplate = async () => {
        const newErrors = validate()
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }
        setErrors({})

        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'createIncentiveTemplate', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    templateName: formData.templateName.trim(),
                    templateDescription: formData.templateDescription.trim(),
                    createdBy: authCtx?.authIdentifier || '',
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                toast.success('Commission plan created. Now add commission rules.')
                // Navigate directly to detail screen
                navigate(`/payroll/incentive-structure/${respo.response.templateIdentifier}`, {
                    replace: true,
                    state: {
                        templateName: respo.response.templateName,
                    },
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if (respo.message?.includes('templateName')) {
                        setErrors((prev) => ({ ...prev, templateName: respo.message }))
                    } else if (respo.message?.includes('templateDescription')) {
                        setErrors((prev) => ({ ...prev, templateDescription: respo.message }))
                    } else if (respo.message?.includes('createdBy')) {
                        toast.error('Session error. Please log out and back in.')
                    } else {
                        toast.error(respo.message)
                    }
                    break
                case 'VAL_002':
                    setErrors((prev) => ({ ...prev, templateName: respo.message }))
                    break
                case 'VAL_004':
                    setErrors((prev) => ({
                        ...prev,
                        templateName: `"${formData.templateName.trim()}" already exists. Try a different name.`,
                    }))
                    break
                default:
                    toast.error(respo?.message || 'Failed to create plan. Please try again.')
                    break
            }
        } catch (error) {
            console.error('createIncentiveTemplate error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    // ─────────────────────────────────────────────────────────────
    return (
        <div className={styles.page}>

            {/* ── Header ── */}
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Commission Plan</span>
                    <h1 className={styles.headerTitle}>Create Plan</h1>
                </div>
            </header>

            <div className={styles.formWrap}>

                {/* ── Context banner ── */}
                <div className={styles.contextBanner}>
                    <p className={styles.contextTitle}>Commission Plan (Variable Pay)</p>
                    <p className={styles.contextBody}>
                        Commission is earned based on performance and computed from actual session
                        revenue. It is separate from the guaranteed fixed salary defined in Salary Structures.
                    </p>
                </div>

                {/* ── Info banner ── */}
                <div className={styles.infoBanner}>
                    <Zap size={15} className={styles.infoBannerIcon} />
                    <p>
                        Commission is auto-computed from session revenue data. This plan defines the rules —
                        percentages, slabs, and bonuses. Fixed salary is managed separately under Salary Structures.
                    </p>
                </div>

                {/* ── Quick-start templates ── */}
                {showQuickStart && (
                    <div className={styles.quickStartSection}>
                        <div className={styles.quickStartHeader}>
                            <p className={styles.quickStartTitle}>Quick Start</p>
                            <button
                                type="button"
                                className={styles.quickStartDismiss}
                                onClick={() => setShowQuickStart(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <p className={styles.quickStartSubtitle}>
                            Choose a template to auto-fill the form
                        </p>
                        <div className={styles.quickStartGrid}>
                            {QUICK_START_PLANS.map((plan) => (
                                <button
                                    key={plan.label}
                                    type="button"
                                    className={styles.quickStartCard}
                                    onClick={() => applyQuickStart(plan)}
                                >
                                    <p className={styles.quickStartCardLabel}>{plan.label}</p>
                                    <p className={styles.quickStartCardDesc}>{plan.description}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* ════════ Plan name field ════════ */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="templateName">
                        Plan Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="templateName"
                        className={`${styles.textInput} ${errors.templateName ? styles.textInputError : ''}`}
                        type="text"
                        maxLength={200}
                        placeholder="e.g. Senior Stylist Commission Plan, Trainee Starter Plan"
                        value={formData.templateName}
                        onChange={(e) => handleChange('templateName', e.target.value)}
                        spellCheck={false}
                    />
                    {nameCharCount > 150 && (
                        <p className={styles.charCount}>{nameCharCount}/200</p>
                    )}
                    {errors.templateName && (
                        <p className={styles.fieldError}>
                            <AlertTriangle size={12} /> {errors.templateName}
                        </p>
                    )}
                </div>

                {/* ════════ Description field ════════ */}
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel} htmlFor="templateDescription">
                        Description <span className={styles.required}>*</span>
                    </label>
                    <textarea
                        id="templateDescription"
                        className={`${styles.textarea} ${errors.templateDescription ? styles.textInputError : ''}`}
                        rows={4}
                        placeholder="Describe the commission structure — who it applies to, what the target is, and what type of commission is included..."
                        value={formData.templateDescription}
                        onChange={(e) => handleChange('templateDescription', e.target.value)}
                        spellCheck={false}
                    />
                    {descCharCount > 400 && (
                        <p className={styles.charCount}>{descCharCount}/1000</p>
                    )}
                    {errors.templateDescription && (
                        <p className={styles.fieldError}>
                            <AlertTriangle size={12} /> {errors.templateDescription}
                        </p>
                    )}
                </div>

                {/* ── Post-creation info ── */}
                <div className={styles.infoCard}>
                    <Info size={14} className={styles.infoCardIcon} />
                    <p>
                        After creation, you'll add commission components (SERVICE_COMMISSION, CATEGORY_COMMISSION,
                        SLAB_COMMISSION, TARGET_BONUS, etc.) to define how earnings are computed. You can always
                        edit or archive this plan later.
                    </p>
                </div>

                {/* ════════ Create button ════════ */}
                <button
                    type="button"
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    disabled={isSaving}
                    onClick={createIncentiveTemplate}
                >
                    {isSaving ? (
                        <><LoaderCircle size={16} className={styles.spin} /> Creating…</>
                    ) : (
                        <>Create Commission Plan →</>
                    )}
                </button>

            </div>
        </div>
    )
}

export default CreateIncentiveTemplateScreen